import { useMemo } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { RoleLabelsMap } from "@workflow/types";

import { usePessoaDetail } from "@/hooks/pessoas/use-pessoa-detail";
import { usePermissions } from "@/hooks/use-permissions";

export const LAST_LOGIN_OVERDUE_THRESHOLD_DAYS = 30;
export const MAX_FUNCOES_VISIVEIS = 2;

export type PersonStatus = "ativo" | "ausente" | "inativo";

function isLoginOverdue(ultimoLogin?: string): boolean {
  if (!ultimoLogin) return false;
  const limiteMs = LAST_LOGIN_OVERDUE_THRESHOLD_DAYS * 24 * 60 * 60 * 1000;
  return Date.now() - new Date(ultimoLogin).getTime() > limiteMs;
}

function roleLabel(nome: string): string {
  return RoleLabelsMap[nome] || nome;
}

/**
 * View-model do perfil de pessoa: concentra busca de dados, permissões e
 * regras de derivação, entregando aos componentes apenas valores prontos.
 */
export function usePersonProfile(pessoaId: number | null) {
  const { pessoa, isLoading } = usePessoaDetail(pessoaId ?? undefined);
  const { isAdmin } = usePermissions();

  return useMemo(() => {
    if (!pessoa) {
      return { pessoa: undefined, isLoading } as const;
    }

    const isExColaborador = !pessoa.ativo;
    const isAusente = !!pessoa.ausente;
    const status: PersonStatus = isExColaborador ? "inativo" : isAusente ? "ausente" : "ativo";

    const funcoesOrdenadas = [...(pessoa.roles ?? [])]
      .sort((a, b) => roleLabel(a.nome).localeCompare(roleLabel(b.nome)))
      .map((role) => ({ id: role.id, label: roleLabel(role.nome) }));
    const squadsOrdenados = [...(pessoa.squads ?? [])].sort((a, b) => a.nome.localeCompare(b.nome));

    const showAdminSecurity =
      isAdmin && (pessoa.abrangenciaNivel !== undefined || pessoa.sensibilidadeNivel !== undefined);

    const membroDesde = format(new Date(pessoa.criadoEm || Date.now()), "MMMM 'de' yyyy", {
      locale: ptBR,
    });
    const ultimoAcessoLabel = pessoa.ultimoLogin
      ? `Último acesso em ${format(new Date(pessoa.ultimoLogin), "d 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })}`
      : "Sem registros de acesso";

    return {
      pessoa,
      isLoading,
      isAdmin,
      isExColaborador,
      isAusente,
      status,
      isLastLoginOverdue: isLoginOverdue(pessoa.ultimoLogin),
      funcoesOrdenadas,
      squadsOrdenados,
      showAdminSecurity,
      membroDesde,
      ultimoAcessoLabel,
    } as const;
  }, [pessoa, isLoading, isAdmin]);
}
